import { PaginatedList } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { Card, ServerPagination, StatusPill } from "@taunggyi-plus/design-system";
type Job = {
  id: string;
  status: string;
  created_at: string;
  finished_at: string | null;
  filename: string | null;
  bytes: number | null;
  checksum: string | null;
  message: string | null;
};
export function AdminBackupPage() {
  const { client } = useAuth();
  const [history, setHistory] = useState<{ items: Job[]; total: number }>({ items: [], total: 0 });
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useFeedback();
  const load = useCallback(async () => {
    if (!client) return;
    const result = await client.rpc("database_backup_history", { target_page: page });
    if (result.error) throw new Error(result.error.message);
    setHistory((old) => {
      const next = result.data as typeof history;
      return page === 0
        ? next
        : {
            ...next,
            items: [
              ...(old?.items ?? []).filter((o) => !next.items.some((n) => n.id === o.id)),
              ...next.items,
            ],
          };
    });
  }, [client, page]);
  useEffect(() => {
    let active = true;
    const refresh = () => {
      void load().catch(() => {
        if (active) setMessage("Backup history could not be loaded. Please refresh.");
      });
    };
    const first = setTimeout(refresh, 0);
    const timer = setInterval(refresh, 3000);
    return () => {
      active = false;
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [load, setMessage]);
  async function backup() {
    if (!client || busy) return;
    setBusy(true);
    setMessage("");
    try {
      const result = await client.rpc("request_database_backup", {});
      if (result.error) throw new Error(result.error.message);
      setPage(0);
      await load();
      setMessage("Backup requested. This page updates automatically.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to request backup.");
    } finally {
      setBusy(false);
    }
  }
  const working = history.items.some((job) => ["queued", "running"].includes(job.status));
  return (
    <div className="role-more-page">
      <a className="more-back-link" href="/more">
        ← More
      </a>
      <Card className="management-card role-more-detail-card">
        <button
          type="button"
          className="button button--primary"
          disabled={busy || working}
          onClick={() => void backup()}
        >
          {busy ? "Requesting…" : working ? "Backup in progress…" : "Backup now"}
        </button>
        <p>Includes database records and accounts. Uploaded files are not included.</p>
        {message && <p role="status">{message}</p>}
      </Card>
      <h2>Backup history</h2>
      <PaginatedList
        pagination="external"
        items={history.items}
        label="Backup history"
        renderItem={(job) => (
          <Card className="management-card" key={job.id}>
            <StatusPill
              tone={
                job.status === "completed"
                  ? "success"
                  : job.status === "failed"
                    ? "warning"
                    : "neutral"
              }
            >
              {job.status}
            </StatusPill>
            <p>Requested {new Date(job.created_at).toLocaleString()}</p>
            {job.finished_at && <p>Finished {new Date(job.finished_at).toLocaleString()}</p>}
            {job.message && <p>{job.message}</p>}
            {job.filename && (
              <p style={{ overflowWrap: "anywhere" }}>
                {job.filename} · {((job.bytes ?? 0) / 1024 / 1024).toFixed(2)} MB
              </p>
            )}
            {job.checksum && (
              <details>
                <summary>SHA-256 checksum</summary>
                <p style={{ overflowWrap: "anywhere" }}>{job.checksum}</p>
              </details>
            )}
          </Card>
        )}
      />
      {!history.total && <p>No manual backups yet.</p>}
      <ServerPagination page={page} total={history.total} onPage={setPage} />
    </div>
  );
}
